import { action, computed, observable } from 'mobx';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import { isCX } from '~/utils/client-experience.utils';
import {
    FlowStatus,
    ICanceled,
    IFlow,
    IFlowConfigurationMember,
    IFlowSummary,
    IFlowTheme,
    IFlowPublicConfiguration,
    IVendorEmail,
    LeadFormStatus,
    TBlockType,
    TFlowStatus,
    TGratuityVariation,
    FlowSourceType
} from '~/types/model.types';
import {
    Currency,
    IMember,
    IPendingChange,
    IVariableResponse,
    TFlowType
} from '~/types/server.types';
import { TRootStore } from '~/types/app.types';
import {
    LEAD_FORM_ALLOWED_FILE_TYPES,
    SMART_FILE_ALLOWED_FILE_TYPES
} from '~/common/UploadQuestion/upload-question.constants';
import { TOneComposerSmartFields } from '~/common/SmartFields/smart-fields.types';
import first from 'lodash/first';

export class BaseFlow<T extends TRootStore> {
    rootStore: T;

    @observable _id: string;
    @observable _theme: IFlowTheme;
    @observable activeRevision: FlowRevision<T>;
    @observable canceled: ICanceled;
    @observable changes_pending_approval: IPendingChange[];
    @observable company_id: string;
    @observable configuration: FlowConfigurationModel;
    @observable currency: Currency;
    @observable draftRevision: FlowRevision<T>;
    @observable expired: boolean;
    @observable first_published_at: string;
    @observable source: FlowSourceType;
    @observable sent_on: string;
    @observable submitted_on: string;
    @observable flow_type: TFlowType;
    @observable is_published: boolean;
    @observable card_on_file_enabled: boolean;
    @observable is_template: boolean;
    @observable members: IMember[];
    @observable project_id: string;
    @observable status: TFlowStatus;
    @observable summary: IFlowSummary;
    @observable title: string;
    @observable workspace_id: string;
    @observable share_flow_pending_task_data: IVendorEmail;
    @observable version_number: number;
    @observable deactivated_public_flow: boolean;
    @observable gratuity_variation: TGratuityVariation;
    @observable is_eligible_for_mark_as_completed: boolean;
    @observable public_flow_workflow_id?: string;
    @observable company_team_members?: IFlowConfigurationMember[];
    @observable smart_fields?: TOneComposerSmartFields;
    @observable public_configuration?: IFlowPublicConfiguration;
    @observable is_selling_template?: boolean;
    @observable submitted_by_user?: { first_name?: string; last_name?: string };

    constructor(flow: IFlow, rootStore: T) {
        if (!flow) {
            return;
        }

        this.rootStore = rootStore;
        this._id = flow._id;
        this._theme = flow.theme;
        this.canceled = flow.canceled;
        this.changes_pending_approval = flow.changes_pending_approval;
        this.company_id = flow.company_id;
        this.configuration = new FlowConfigurationModel(flow.configuration);
        this.currency = flow.currency;
        this.expired = flow.expired;
        this.first_published_at = flow.first_published_at;
        this.is_published = flow.is_published;
        this.flow_type = flow.flow_type;
        this.is_template = flow.is_template;
        this.members = flow.members;
        this.project_id = flow.project_id;
        this.status = flow.status;
        this.summary = flow.summary;
        this.title = flow.title;
        this.workspace_id = flow.workspace_id;
        this.version_number = flow.version_number;
        this.share_flow_pending_task_data = flow.share_flow_pending_task_data;
        this.deactivated_public_flow = flow.deactivated_public_flow;
        this.gratuity_variation = flow.gratuity_variation;
        this.is_eligible_for_mark_as_completed =
            flow.is_eligible_for_mark_as_completed;
        this.company_team_members = flow.company_team_members;
        this.smart_fields = flow.smart_fields;
        this.submitted_by_user = flow.submitted_by_user;
    }

    // #region computed

    @computed
    get gratuityVariation() {
        return this.gratuity_variation || 'control';
    }

    @computed
    get isLeadForm() {
        return this.flow_type === 'public_flow';
    }

    @computed
    get isInstance() {
        return !this.is_template && !this.isLeadForm;
    }

    @computed
    get isWorkflowActivated() {
        return this.configuration.is_workflow_activated;
    }

    @computed
    get leadFormProjectTagIds() {
        return this.configuration.lead_form_project_tag_ids || [];
    }

    @computed
    get leadFormContactTagIds() {
        return this.configuration.lead_form_contact_tag_ids || [];
    }

    @computed
    get isEmailConfirmationActivated() {
        return this.configuration.is_email_confirmation_activated;
    }

    @computed
    get hasPendingChanges() {
        return this.changes_pending_approval?.some(ch => !ch.accepted);
    }

    @computed
    get workflowId() {
        return this.public_flow_workflow_id;
    }

    @computed
    get publicStatus() {
        if (this.flow_type !== 'public_flow') {
            return undefined;
        }
        if (this.deactivated_public_flow) {
            return LeadFormStatus.Inactive;
        }
        if (this.is_published) {
            return LeadFormStatus.Live;
        }

        return LeadFormStatus.Draft;
    }

    @computed
    get isLeadFormLive() {
        return this.isLeadForm && this.publicStatus === LeadFormStatus.Live;
    }

    @computed
    get isLeadFormDraft() {
        return this.isLeadForm && this.publicStatus === LeadFormStatus.Draft;
    }

    @computed
    get isPublicTemplateSelling() {
        return (
            this.isLeadForm &&
            this.publicStatus === LeadFormStatus.Live &&
            this.is_selling_template
        );
    }

    @computed
    get isLeadFormInactive() {
        return this.isLeadForm && this.publicStatus === LeadFormStatus.Inactive;
    }

    @computed
    get isTemplate() {
        return this.is_template;
    }

    @computed
    get isFlowCompleted() {
        return this.status === FlowStatus.Completed;
    }

    @computed
    get revision() {
        if (isCX(this.rootStore)) {
            return this.activeRevision;
        }
        return this.draftRevision;
    }

    @computed
    get pages() {
        return this.revision?.pages;
    }

    @computed
    get pagesMap() {
        return this.revision?.pagesMap;
    }

    @computed
    get blocks() {
        return this.revision?.blocks;
    }

    @computed
    get variables() {
        return this.revision?.variables;
    }

    @computed
    get flowRecipients() {
        return this.members.filter(m => this.company_id !== m.company?._id);
    }

    @computed
    get flowVendorsList() {
        return this.members.filter(
            member => this.company_id === member.company?._id
        );
    }

    @computed
    get flowCollaboratorList() {
        return this.members.filter(
            member =>
                this.company_id !== member.company?._id &&
                member.system_user_type === 'vendor'
        );
    }

    @computed
    get uploadQuestionAcceptedFileTypes() {
        return this.isLeadForm
            ? LEAD_FORM_ALLOWED_FILE_TYPES
            : SMART_FILE_ALLOWED_FILE_TYPES;
    }

    @computed
    get vendor() {
        // TODO: will this return the correct vendor?
        return first(
            this.members.filter(m => this.company_id === m.company?._id)
        );
    }

    // #endregion computed

    // #region methods
    isFlowClient(clientId) {
        return this.flowRecipients.find(recipient => {
            return recipient._id === clientId;
        });
    }

    isTeamMember(teamMemberId) {
        return this.flowVendorsList.find(vendor => {
            return teamMemberId === vendor._id;
        });
    }

    getBlocksByType(blockType: TBlockType) {
        return this.blocks.filter(block => block.type === blockType);
    }

    // #endregion methods

    // #region actions
    @action
    setActiveRevision(flowRevision: FlowRevision<T>) {
        this.activeRevision = flowRevision;
    }

    @action
    setDraftRevision(flowRevision: FlowRevision<T>) {
        this.draftRevision = flowRevision;
    }

    @action
    addVariables(variables: IVariableResponse[]) {
        this.revision.addVariables(variables);
    }

    @action
    removeVariables(variables: IVariableResponse[]) {
        this.revision.addVariables(variables);
    }

    // #endregion actions
}
